/**
 * The clarification thread.
 *
 * The rule that makes this work is on the API side and worth restating on screen: **who replies
 * decides where the record goes**. An answer from the other side returns it to whoever asked; a
 * follow-up from the asker parks it again. So the person looking at this always knows whether
 * the ball is theirs.
 *
 * One open thread at a time, enforced by a partial unique index — so this never has to reason
 * about two concurrent questions.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ErrorNote } from '@/components/bits';
import { Badge, Button, Card, Field, Textarea } from '@/components/ui';
import { formatInstant } from '@/lib/dates';
import type { ClarificationThread } from '@/lib/types';

export function Clarifications({
  threads,
  canAnswer,
  onReply,
  onClose,
  invalidate,
}: {
  threads: readonly ClarificationThread[];
  canAnswer: boolean;
  onReply: (clarificationId: number, body: string) => Promise<unknown>;
  onClose?: (clarificationId: number) => Promise<unknown>;
  invalidate: unknown[];
}) {
  const cache = useQueryClient();
  const [drafts, setDrafts] = useState<Record<number, string>>({});

  const reply = useMutation({
    mutationFn: (input: { id: number; body: string }) => onReply(input.id, input.body),
    onSuccess: async (_result, input) => {
      setDrafts((current) => ({ ...current, [input.id]: '' }));
      await cache.invalidateQueries({ queryKey: invalidate });
    },
  });

  const close = useMutation({
    mutationFn: (clarificationId: number) =>
      onClose ? onClose(clarificationId) : Promise.resolve(),
    onSuccess: () => cache.invalidateQueries({ queryKey: invalidate }),
  });

  if (threads.length === 0) return null;

  return (
    <Card title="Questions">
      <ul className="grid gap-6">
        {threads.map((thread) => (
          <li key={thread.id} className="grid gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                tone={
                  thread.status === 'closed'
                    ? 'neutral'
                    : thread.status === 'answered'
                      ? 'good'
                      : 'warn'
                }
              >
                {thread.status}
              </Badge>
              <span className="text-muted-foreground text-xs">
                opened {formatInstant(thread.opened_at)}
              </span>
            </div>

            <ol className="grid gap-3">
              {thread.messages.map((message) => (
                <li key={message.id} className="border-border border-l-2 pl-3 text-sm">
                  <p className="text-muted-foreground text-xs">
                    user #{message.author_user_id} · {formatInstant(message.created_at)}
                  </p>
                  <p className="mt-0.5 whitespace-pre-wrap">{message.body}</p>
                  {message.link_url ? (
                    <a
                      href={message.link_url}
                      className="text-xs underline underline-offset-4"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {message.link_url}
                    </a>
                  ) : null}
                </li>
              ))}
            </ol>

            {thread.status !== 'closed' ? (
              <div className="grid gap-2">
                {canAnswer || thread.status === 'answered' ? (
                  <>
                    <Field label="Reply">
                      {(props) => (
                        <Textarea
                          {...props}
                          value={drafts[thread.id] ?? ''}
                          onChange={(event) =>
                            setDrafts({ ...drafts, [thread.id]: event.target.value })
                          }
                        />
                      )}
                    </Field>
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        busy={reply.isPending}
                        disabled={(drafts[thread.id] ?? '').trim() === ''}
                        onClick={() =>
                          reply.mutate({ id: thread.id, body: (drafts[thread.id] ?? '').trim() })
                        }
                      >
                        Send
                      </Button>
                      {onClose ? (
                        <Button
                          size="sm"
                          busy={close.isPending}
                          onClick={() => close.mutate(thread.id)}
                        >
                          Close this question
                        </Button>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    Waiting on the other side to answer.
                  </p>
                )}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {reply.error ? <ErrorNote error={reply.error} /> : null}
      {close.error ? <ErrorNote error={close.error} /> : null}
    </Card>
  );
}
